import { db } from "@/lib/firebase";
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from "firebase/firestore";
import { Office, OfficeFormData, DEFAULT_OFFICE_ATTENDANCE, DEFAULT_OFFICE_HOLIDAYS } from "@/types/office";

const OFFICES_COLLECTION = "offices";

/** Helper to safely convert any date format to Date object */
function toDate(val: any): Date {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
  if (typeof val === 'number') return new Date(val);
  if (typeof val === 'string') return new Date(val);
  return new Date();
}

/** Map Firestore document data to Office type */
function mapOfficeDoc(docId: string, data: any): Office {
  return {
    id: docId,
    name: data.name || '',
    city: data.city || '',
    address: data.address || '',
    // Default geoLocation to the (0,0) sentinel when absent, so offices created
    // by the dashboard app without coordinates don't crash. All UI consumers
    // must test for the (0,0) sentinel explicitly (not truthiness of latitude)
    // to detect "not configured". Keeping the object shape avoids cascading
    // type-system changes across all callers.
    geoLocation: data.geoLocation ?? { latitude: 0, longitude: 0 },
    isActive: data.isActive ?? true,
    attendanceSettings: data.attendanceSettings ?? DEFAULT_OFFICE_ATTENDANCE,
    holidayConfig: data.holidayConfig ?? DEFAULT_OFFICE_HOLIDAYS,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
    createdBy: data.createdBy || '',
  };
}

/**
 * Get all offices
 */
export async function getAllOffices(): Promise<Office[]> {
  try {
    const officesRef = collection(db, OFFICES_COLLECTION);
    const q = query(officesRef, orderBy("name", "asc"));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(d => mapOfficeDoc(d.id, d.data()));
  } catch (error) {
    console.error("Error fetching offices:", error);
    throw new Error("Failed to fetch offices");
  }
}

/**
 * Get active offices only
 */
export async function getActiveOffices(): Promise<Office[]> {
  try {
    const officesRef = collection(db, OFFICES_COLLECTION);
    const q = query(
      officesRef, 
      where("isActive", "==", true),
      orderBy("name", "asc")
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(d => mapOfficeDoc(d.id, d.data()));
  } catch (error) {
    console.error("Error fetching active offices:", error);
    throw new Error("Failed to fetch active offices");
  }
}

/**
 * Get office by ID
 */
export async function getOfficeById(officeId: string): Promise<Office | null> {
  try {
    const officeRef = doc(db, OFFICES_COLLECTION, officeId);
    const officeDoc = await getDoc(officeRef);
    
    if (!officeDoc.exists()) {
      return null;
    }
    
    const data = officeDoc.data();
    return mapOfficeDoc(officeDoc.id, data);
  } catch (error) {
    console.error("Error fetching office:", error);
    throw new Error("Failed to fetch office");
  }
}

/**
 * Get offices by city
 */
export async function getOfficesByCity(city: string): Promise<Office[]> {
  try {
    const officesRef = collection(db, OFFICES_COLLECTION);
    const q = query(
      officesRef,
      where("city", "==", city),
      where("isActive", "==", true),
      orderBy("name", "asc")
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(d => mapOfficeDoc(d.id, d.data()));
  } catch (error) {
    console.error("Error fetching offices by city:", error);
    throw new Error("Failed to fetch offices by city");
  }
}

/**
 * Create a new office
 */
export async function createOffice(
  officeData: OfficeFormData,
  userId: string
): Promise<string> {
  try {
    const officesRef = collection(db, OFFICES_COLLECTION);
    
    const newOffice = {
      name: officeData.name,
      city: officeData.city,
      address: officeData.address,
      geoLocation: {
        latitude: officeData.latitude,
        longitude: officeData.longitude,
      },
      attendanceSettings: officeData.attendanceSettings ?? DEFAULT_OFFICE_ATTENDANCE,
      holidayConfig: officeData.holidayConfig ?? DEFAULT_OFFICE_HOLIDAYS,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId,
    };
    
    const docRef = await addDoc(officesRef, newOffice);
    return docRef.id;
  } catch (error) {
    console.error("Error creating office:", error);
    throw new Error("Failed to create office");
  }
}

/**
 * Update an existing office
 */
export async function updateOffice(
  officeId: string,
  officeData: Partial<OfficeFormData>
): Promise<void> {
  try {
    const officeRef = doc(db, OFFICES_COLLECTION, officeId);
    
    const updateData: any = {
      updatedAt: serverTimestamp(),
    };
    
    if (officeData.name !== undefined) updateData.name = officeData.name;
    if (officeData.city !== undefined) updateData.city = officeData.city;
    if (officeData.address !== undefined) updateData.address = officeData.address;
    if (officeData.latitude !== undefined && officeData.longitude !== undefined) {
      updateData.geoLocation = {
        latitude: officeData.latitude,
        longitude: officeData.longitude,
      };
    }
    if (officeData.attendanceSettings !== undefined) {
      updateData.attendanceSettings = officeData.attendanceSettings;
    }
    if (officeData.holidayConfig !== undefined) {
      updateData.holidayConfig = officeData.holidayConfig;
    }
    
    await updateDoc(officeRef, updateData);
  } catch (error) {
    console.error("Error updating office:", error);
    throw new Error("Failed to update office");
  }
}

/**
 * Delete an office (soft delete by setting isActive to false)
 */
export async function deleteOffice(officeId: string): Promise<void> {
  try {
    const officeRef = doc(db, OFFICES_COLLECTION, officeId);
    await updateDoc(officeRef, {
      isActive: false,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error deleting office:", error);
    throw new Error("Failed to delete office");
  }
}

/**
 * Permanently delete an office (hard delete)
 */
export async function permanentlyDeleteOffice(officeId: string): Promise<void> {
  try {
    const officeRef = doc(db, OFFICES_COLLECTION, officeId);
    await deleteDoc(officeRef);
  } catch (error) {
    console.error("Error permanently deleting office:", error);
    throw new Error("Failed to permanently delete office");
  }
}

/**
 * Activate an office
 */
export async function activateOffice(officeId: string): Promise<void> {
  try {
    const officeRef = doc(db, OFFICES_COLLECTION, officeId);
    await updateDoc(officeRef, {
      isActive: true,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error activating office:", error);
    throw new Error("Failed to activate office");
  }
}
